import {Emitter, Object3D, Scene, TextComponent, WonderlandEngine} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {
    NetworkConfigurationComponent,
    NetworkedComponent,
    networkManager
} from '@wonderlandcloud/client';
import { CustomClient } from './networkextends/CustomClient.js';
import {WonderlandWebsocketEvent} from '@wonderlandcloud/client';
import {vec3, quat} from 'gl-matrix';
import { Events } from './network-events.js';
import Logger from './utils/logger.js';
import { applySharedStyles } from './utils/shared-styles.js';
import { NetworkedGrabbable } from './networked-grabbable.js';
import { EToyDefinition, EToyType } from './etoy/etoy-types.js';
import { Orchestrated } from './orchestrated.js';
import { NetworkListenVideoTexture } from './networklisten-video-texture.js';
import { SharedMediaController } from './media/shared-media-controller.js';
import type { SharedUrlVideoShare } from './media/url-video-sync.js';
import { MediasoupSfuClient } from './network/mediasoup-sfu.js';
import { configureLogger } from './logger-config.js';
import { UserMessaging } from './utils/user-messaging.js';
import { HapticInfluenceDisplay } from './utils/haptic-influence-display.js';
import { NotificationTextPositioner } from './NotificationTextPositioner.js';
import { CopyRotation, CopyHeadNod, CopyPosition } from './copy-rotation.js';
import { VrBodyFollow } from './vr-body-follow.js';
import { setComponentsActive } from './utils/activate-children.js';
import {
    EVENTS_CLIENT_MODAL_CSS,
    createAgeModalContainer,
    createUserModalContainer,
    openAgeVerificationModal as openAgeVerificationModalUi,
    openUsernameModal as openUsernameModalUi,
} from './ui/events-client-modals.js';
import type { VrPreAuthMenuState } from './menus/renderers/VrMenuRenderer.js';
import {
    registerToy as registerToyManager,
    removeToy as removeToyManager,
    removeAllToys as removeAllToysManager,
} from './etoy/toy-manager.js';
import {
    acceptVidshareOffer as acceptVidshareOfferVideo,
    claimMasterShareRole as claimMasterShareRoleVideo,
    createVidrecPeerConnection as createVidrecPeerConnectionVideo,
    createVidsharePeerConnection as createVidsharePeerConnectionVideo,
    createVidsharePeerConnections as createVidsharePeerConnectionsVideo,
    createVidshareOffer as createVidshareOfferVideo,
    destroyVidrecConnection as destroyVidrecConnectionVideo,
    destroyVidshareConnection as destroyVidshareConnectionVideo,
    handleVidshareOffer as handleVidshareOfferVideo,
    startVidshareNegotiation as startVidshareNegotiationVideo,
    startVidshareNegotiations as startVidshareNegotiationsVideo,
    stopVidshare as stopVidshareVideo,
} from './network/video-share.js';
import { handleNetworkEvent } from './network/events-handler.js';
import { SceneController } from './scene-controller.js';
import { register } from 'module';
import { UserSettingsStore } from './utils/user-settings-store.js';
import { getAvatarEntryById, normalizeAvatarId as normalizeCatalogAvatarId } from './avatar/avatar-catalog.js';
import { resolveAvatarTrackingObjects } from './avatar/avatar-tracking.js';
import { RiggedAvatarFollow } from './avatar/rigged-avatar-follow.js';

const MODULE = 'EventsClient';

export function setCookie(cname: string, cvalue: string, exdays: number) {
    UserSettingsStore.setCookie(cname, cvalue, exdays);
}

export function getCookie(cname: string) {
    return UserSettingsStore.getCookie(cname);
}

const tempVec = vec3.create();

// Component-specific styles (shared styles imported from shared-styles.ts)
const CSS = EVENTS_CLIENT_MODAL_CSS;

export type ProstheticType = 'none' | 'inny' | 'outy';

export interface ProstheticState {
    type: ProstheticType;
    networkId: number | null;
}

export interface AvatarState {
    avatarId: string;
}

/**
 * custom-event-handler
 */
export class EventsClient extends NetworkConfigurationComponent {
    static TypeName = 'events-client';

    /** Opt-in two-second snapshots for comparing sent/received poses and IK reach. */
    @property.bool(false)
    debugAvatarTracking = false;

    private avatarTrackingDebugSeconds = 0;

    @property.object()
    nonvrLeftHand!: Object3D;

    @property.object()
    nonvrRightHand!: Object3D;    

    @property.object()
    vrPlayerObject!: Object3D;

    @property.object()
    vrControllerLeft!: Object3D;

    @property.object()
    vrControllerRight!: Object3D;

    @property.object()
    playerPrototype!: Object3D;

    @property.object()
    notificationsObject!: Object3D;

    @property.object()
    hapticDisplayObject!: Object3D;

    @property.object()
    radioObject!: Object3D;

    @property.object()
    toyBallPrefab!: Object3D;    
    
    @property.object()
    toyProximityVibePrefab!: Object3D;

    @property.object()
    toyOscillatingRotPrefab!: Object3D;

    @property.object()
    localProstheticInny!: Object3D;

    @property.object()
    localProstheticOuty!: Object3D;

    @property.object()
    remoteProstheticInnyTemplate!: Object3D;

    @property.object()
    remoteProstheticOutyTemplate!: Object3D;

    @property.object()
    avatarTemplates!: Object3D;

    @property.bool()
    serverBasedBroadcast: boolean = false;

    static InheritProperties = true;

    notificationText?: TextComponent | null;
    hapticDisplayText?: TextComponent | null;
    private hapticDisplayEnabled: boolean = false;

    username: string = '';
    room: string = '';
    /** Room password — kept in memory only, never persisted */
    private roomPassword: string = '';
    sharemic: boolean = true;
    headlessmode: boolean = false;
    avatarUrl: string = '';
    private selectedAvatarId: string = 'default_sim';
    /** JWT auth token — stored in memory only, never persisted to cookies/localStorage */
    private authToken: string | null = null;
    private preAuthStage: 'hidden' | 'age' | 'login' = 'hidden';
    private preAuthShowLoginError = false;
    private preAuthShowRoomPasswordError = false;
    private preAuthRoomJoinError = '';
    readonly onPreAuthStateChanged: Emitter<[]> = new Emitter();

    agemodal!: HTMLDivElement;
    usermodal!: HTMLDivElement;
    frame!: HTMLIFrameElement;

    _serverNetworkId: number = -100;
    playerNetworkId: number = -1;
    playerUserId: number = -1;
    /** Set exclusively by the SET_ID event — never pre-populated with a networkObjectId. */
    serverAssignedUserId: number = -1;

    remoteUsernames: Map<number, string> = new Map<number, string>();
    remoteUserJoinData: Map<number, any> = new Map<number, any>();
    toys: Map<number, any> = new Map<number, any>();
    toyInstances: Map<number, Object3D> = new Map<number, Object3D>();
    private localProstheticState: ProstheticState = { type: 'none', networkId: null };

    vidsharePeerConnections: Map<number, RTCPeerConnection> = new Map<number, RTCPeerConnection>();
    vidrecPeerConnections: Map<number, RTCPeerConnection> = new Map<number, RTCPeerConnection>();
    webRTCSupported: boolean = true;
    vidshareCandidatesMap: Map<number, RTCIceCandidateInit[]> = new Map<number, RTCIceCandidateInit[]>;
    vidrecCandidatesMap: Map<number, RTCIceCandidateInit[]> = new Map<number, RTCIceCandidateInit[]>;
    private receiveLoggingAttached = false;
    makingVidshareOffer: boolean = false;
    sharingVideo: boolean = false;
    onShareEvent: Emitter<[number, RTCTrackEvent]> = new Emitter<[number, RTCTrackEvent]>();
    onShareStopEvent: Emitter<[number]> = new Emitter<[number]>();
    onUserJoinEvent: Emitter<[number]> = new Emitter<[number]>();
    onClaimMasterShareEvent: Emitter<[number]> = new Emitter<[number]>();
    onSyncUrlVideoStartEvent: Emitter<[{
        url: string;
        startAtMs: number;
        requestedBy: number;
        seekSeconds: number;
    }]> = new Emitter();
    onSyncUrlVideoPingEvent: Emitter<[{
        url: string;
        startAtMs: number;
        requestedBy: number;
        seekSeconds: number;
    }]> = new Emitter();
    masterSharerId: number = 0;
    private sceneSwitchCooldownUntilMs: number = 0;

    // Track active video streams by sender ID for late-joining listeners
    activeVideoStreams: Map<number, MediaStream> = new Map<number, MediaStream>();

    /** Latest room-wide URL share, replayed to video surfaces that start after it began. */
    activeUrlVideoShare: SharedUrlVideoShare | null = null;

    private mediasoupClient: MediasoupSfuClient | null = null;
    private mediasoupAudioElement: HTMLAudioElement | null = null;
    private p2pRemoteAudioElements: Map<number, HTMLAudioElement> = new Map<number, HTMLAudioElement>();
    private p2pRemoteAudioTrackListeners: Map<number, { stream: MediaStream; handler: (event: MediaStreamTrackEvent) => void }> = new Map<number, { stream: MediaStream; handler: (event: MediaStreamTrackEvent) => void }>();
    private broadcastShareAudioMixGain: number = 1;
    private mediasoupUnavailableOnServer: boolean = false;
    private mediasoupUnavailableLogged: boolean = false;

    private readonly onShareTrackForAudio = (senderId: number, e: RTCTrackEvent) => {
        this.handleP2PShareTrackForAudio(senderId, e);
    };
    private readonly onShareStopForAudio = (senderId: number) => {
        this.removeP2PAudioElement(senderId);
    };

    // Map of networkId -> NetworkedGrabbable component for synchronized grab objects
    networkedGrabbables: Map<number, NetworkedGrabbable> = new Map<number, NetworkedGrabbable>();
    networkedOrchestrated: Map<number, Orchestrated> = new Map<number, Orchestrated>();

    // Connection state and message queue for orchestrated info requests
    private isConnected: boolean = false;
    private pendingOrchestratedRequests: Array<{nametag: string, transform: number[]}> = [];

    // TURN server configuration for video peer connections
    private turnIceServers: RTCIceServer[] | null = null;
    private turnIceServersPromise: Promise<RTCIceServer[] | null> | null = null;
    private turnIceServersFetchedAt: number | null = null;

    // Track bound onEvent handler to prevent duplicate registrations
    private boundOnEvent: ((e: WonderlandWebsocketEvent) => void) | null = null;

    /** Saved reference to the onPostRender binding — needed to remove it on reconnect. */
    private boundNetworkUpdate: (() => void) | null = null;

    /** One-shot callback fired by onSuccessfulConnection, used by switchRoom() to signal completion. */
    private _onNextConnection: (() => void) | null = null;

    public get serverNetworkId(): number {
        return this._serverNetworkId;
    }

    /** Returns the JWT token obtained at login, or null if not authenticated. */
    public getAuthToken(): string | null {
        return this.authToken;
    }

    public isAuthenticated(): boolean {
        return !!this.authToken;
    }

    public getVrPreAuthMenuState(): VrPreAuthMenuState | null {
        if (this.authToken || this.preAuthStage === 'hidden') {
            return null;
        }

        const statusMessage = this.preAuthShowLoginError
            ? 'Login incorrect. Please try again.'
            : this.preAuthShowRoomPasswordError
                ? 'Room password incorrect. Please try again.'
                : this.preAuthRoomJoinError;

        return {
            stage: this.preAuthStage === 'age' ? 'age' : 'login',
            username: this.username,
            roomName: this.room,
            roomPassword: this.roomPassword,
            sharemic: this.sharemic,
            headlessmode: this.headlessmode,
            statusMessage,
        };
    }

    public updatePreAuthDrafts(partial: {
        username?: string;
        roomName?: string;
        roomPassword?: string;
        sharemic?: boolean;
        headlessmode?: boolean;
    }): void {
        if (typeof partial.username === 'string') {
            this.username = partial.username;
        }
        if (typeof partial.roomName === 'string') {
            this.room = partial.roomName;
        }
        if (typeof partial.roomPassword === 'string') {
            this.roomPassword = partial.roomPassword;
        }
        if (typeof partial.sharemic === 'boolean') {
            this.sharemic = partial.sharemic;
        }
        if (typeof partial.headlessmode === 'boolean') {
            this.headlessmode = partial.headlessmode;
        }
    }

    public confirmPreAuthAgeVerification(): void {
        this.openUsernameModal();
    }

    public async denyPreAuthAgeVerification(): Promise<void> {
        const xrSession = this.engine.xr?.session;
        if (xrSession) {
            try {
                await xrSession.end();
            } catch {
                // Continue to hard-stop access even if XR session end fails.
            }
        }

        alert('You must be 18 years or older to access this application.');
        window.location.href = 'about:blank';
    }

    public async submitPreAuthLogin(values: {
        username: string;
        password: string;
        room: string;
        roomPassword: string;
        sharemic: boolean;
        headlessmode: boolean;
    }): Promise<void> {
        this.username = values.username;
        this.room = values.room;
        this.roomPassword = values.roomPassword;
        this.sharemic = values.sharemic;
        this.headlessmode = values.headlessmode;
        UserSettingsStore.setCookie(UserSettingsStore.Keys.EventsUsername, this.username, 365);
        UserSettingsStore.setCookie(UserSettingsStore.Keys.EventsRoom, this.room, 365);
        UserSettingsStore.setCookie(UserSettingsStore.Keys.EventsShareMic, String(this.sharemic), 365);
        UserSettingsStore.setCookie(UserSettingsStore.Keys.EventsHeadlessMode, String(this.headlessmode), 365);

        try {
            await this.loginWithPassword(values.username, values.password);
            this.hidePreAuthPresentation();
            this.connect();
        } catch {
            this.openUsernameModal(true, false);
        }
    }

    /** Returns the current room name (may be empty string if no room set). */
    public getCurrentRoom(): string {
        return this.room;
    }

    /**
     * Switches the player to a different room.
     * Validates the room join (password check) BEFORE disconnecting from the current room.
     * Throws Error('ROOM_PASSWORD_INCORRECT') if the room is private and the password is wrong.
     * Returns a Promise that resolves once the new connection is established.
     */
    public async switchRoom(newRoom: string, newRoomPassword: string = ''): Promise<void> {
        const token = this.getAuthToken();
        if (!token) throw new Error('Not authenticated');

        // Derive the scene path the same way CustomClient does: strip leading '/' and trailing '/ws'
        const scenePath = this.serverPath ? this.serverPath.replace('/', '').slice(0, -3) : '';

        const startUrl = `${this.getRestApiBaseUrl()}/api/servers/start`;
        const resp = await fetch(startUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                path: scenePath,
                room: newRoom,
                ...(newRoomPassword ? { roomPassword: newRoomPassword } : {}),
            }),
        });

        if (resp.status === 403) {
            throw new Error('ROOM_PASSWORD_INCORRECT');
        }
        if (resp.status === 429) {
            const body = await resp.json().catch(() => ({}));
            if (body?.error === 'ROOM_LIMIT_REACHED') throw new Error('ROOM_LIMIT_REACHED');
            if (body?.error === 'ROOM_CREATE_COOLDOWN') throw new Error('ROOM_CREATE_COOLDOWN');
            throw new Error('ROOM_FULL');
        }
        if (!resp.ok) {
            throw new Error(`Failed to join room: ${resp.status}`);
        }

        // Room join validated — update stored room, persist name to cookie, then reconnect.
        // Tear down the current connection before creating a new one.
        this.room = newRoom;
        this.roomPassword = newRoomPassword;
        UserSettingsStore.setCookie(UserSettingsStore.Keys.EventsRoom, this.room, 365);

        await this.cleanupForRoomSwitch();

        return new Promise<void>((resolve) => {
            this._onNextConnection = resolve;
            this.active = false;
            this.connecting = false;
            this.connect();
        });
    }

    /**
     * Tears down all state from the current room connection so we can cleanly
     * join a new room without ghost avatars or leaked resources.
     *
     * Steps:
     *  1. Close the old WebSocket (triggers server's USER_LEFT broadcast to remaining peers).
     *  2. Wait briefly so the server can propagate the close before we reconnect.
     *  3. Destroy all remote-user Object3Ds and clear their tracking maps.
     *  4. Remove all toy instances and video/audio peers.
     *  5. Reset per-connection IDs and flags.
     */
    private async cleanupForRoomSwitch(): Promise<void> {
        // 1. Close old client's signaling WebSocket — this causes the REST server
        //    to run cleanupClientConnection, which closes the game-server peer,
        //    which calls onUserLeave and broadcasts USER_LEFT to remaining room members.
        const oldWs: WebSocket | undefined = (networkManager.client as any)?.ws;
        if (oldWs && oldWs.readyState === WebSocket.OPEN) {
            oldWs.close(1000, 'room-switch');
        }

        // 2. Give the server ~400 ms to process the close and notify remaining users.
        await new Promise<void>(resolve => setTimeout(resolve, 400));

        // 3. Destroy all remote-user avatar Object3Ds and clear the map.
        for (const userObjects of (this.remoteUsers as Map<any, any>).values()) {
            for (const entry of Object.values(userObjects as Record<string, { object: any; id: number }>)) {
                try {
                    networkManager.removeObject(entry.id);
                    if (!entry.object.isDestroyed) entry.object.destroy();
                } catch { /* ignore errors on already-destroyed objects */ }
            }
        }
        this.remoteUsers.clear();
        this.remoteUserJoinData.clear();

        // 4. Destroy toys (also unregisters their networked-grabbable components).
        this.removeAllToys();

        // 5. Close video peer connections.
        for (const pc of this.vidsharePeerConnections.values()) try { pc.close(); } catch { /* ignore */ }
        for (const pc of this.vidrecPeerConnections.values()) try { pc.close(); } catch { /* ignore */ }
        this.vidsharePeerConnections.clear();
        this.vidrecPeerConnections.clear();
        this.vidshareCandidatesMap.clear();
        this.vidrecCandidatesMap.clear();
        this.activeVideoStreams.clear();
        this.activeUrlVideoShare = null;
        this.makingVidshareOffer = false;
        this.sharingVideo = false;
        this.masterSharerId = 0;

        // 6. Remove all audio elements and mediasoup client.
        if (this.mediasoupAudioElement) {
            this.mediasoupAudioElement.pause();
            this.mediasoupAudioElement.srcObject = null;
            this.mediasoupAudioElement.remove();
            this.mediasoupAudioElement = null;
        }
        this.removeAllP2PAudioElements();
        this.mediasoupClient = null;
        this.mediasoupUnavailableOnServer = false;
        this.mediasoupUnavailableLogged = false;

        // 7. Destroy all NetworkedComponents on the local player objects so that
        //    stale networkIds are removed from writeComponents.  Without this,
        //    on re-join the old networkIds would still be sent in every _updateSend
        //    packet, overwriting other users' transform slots in the server's buffer.
        const destroyOwnNetworkedComponents = (obj: Object3D | null | undefined) => {
            if (!obj || obj.isDestroyed) return;
            for (const comp of obj.getComponents(NetworkedComponent)) {
                try { comp.destroy(); } catch { /* ignore */ }
            }
        };
        destroyOwnNetworkedComponents(this.playerObject);
        destroyOwnNetworkedComponents(this.nonvrLeftHand);
        destroyOwnNetworkedComponents(this.nonvrRightHand);
        destroyOwnNetworkedComponents(this.vrPlayerObject);
        destroyOwnNetworkedComponents(this.vrControllerLeft);
        destroyOwnNetworkedComponents(this.vrControllerRight);
        // Belt-and-suspenders: flush any stragglers that onDeactivate may have missed.
        networkManager.writeComponents.clear();
        // Also reset the receive timestamp so Room 1's fresh broadcasts are not
        // discarded by the stale high-water-mark from Room 2.
        networkManager.lastMessageTimestamp = 0;
        

        // 8. Reset per-connection identity and state.
        this._serverNetworkId = -100;
        this.playerNetworkId = -1;
        this.playerUserId = -1;
        this.serverAssignedUserId = -1;
        this.remoteUsernames.clear();
        this.isConnected = false;
        this.pendingOrchestratedRequests = [];
        this.receiveLoggingAttached = false;
        this.localProstheticState = { type: 'none', networkId: null };
        this.removeLocalProstheticNetworkComponents();
        this.deactivateAllLocalProstheticObjects();

        // 9. Reset local onEvent binding status so it will re-bind on next connection
        // Remove the network event listener if it was registered
        // console.log(`Cleaning up for room switch: boundOnEvent=${!!this.boundOnEvent}, networkManager=${!!networkManager}`);
        // if (this.boundOnEvent && networkManager) {
        //     console.log('Removing bound onEvent listener during room switch cleanup');
        //     networkManager.onEvent.remove(this.boundOnEvent);
        //     this.boundOnEvent = null;
        // }

        Logger.info('Room cleanup complete — ready to connect to new room', MODULE);
    }


    public getRestApiBaseUrl(): string {
        const protocol = this.secure === false ? 'http' : 'https';
        const port = this.serverPort ? `:${this.serverPort}` : '';
        const appRoot = this.serverPath
            ? this.serverPath.replace(/\/ws(\/.*)?$/, '')
            : '';
        return `${protocol}://${this.serverHost}${port}${appRoot}`;
    }

    /**
     * Public accessor for TURN ice servers (used by video sharing peer connections)
     */
    public getTurnIceServers(): RTCIceServer[] | null {
        if (!this.turnIceServers) {
            return null;
        }
        if (this.isTurnConfigExpired(this.turnIceServers)) {
            return null;
        }
        return this.turnIceServers;
    }

    private ensureMediasoupClient(): MediasoupSfuClient | null {
        const client = networkManager.client as CustomClient | undefined;
        if (!client) {
            Logger.warn(MODULE, 'Cannot init mediasoup: network client not ready');
            return null;
        }

        if (!this.mediasoupClient) {
            this.mediasoupClient = new MediasoupSfuClient(
                client,
                this.handleMediasoupVideoTrack.bind(this),
                this.handleMediasoupAudioTrack.bind(this),
                this.handleMediasoupProducerClosed.bind(this)
            );
        }

        return this.mediasoupClient;
    }

    private async requestMasterShareApproval(timeoutMs: number = 5000): Promise<boolean> {
        if (this.masterSharerId === this.playerUserId) {
            return true;
        }

        const client = networkManager.client;
        if (!client) {
            Logger.warn(MODULE, 'Cannot request master share approval: network client not ready');
            return false;
        }

        return new Promise<boolean>((resolve) => {
            const onClaim = (claimedId: number) => {
                if (claimedId === this.playerUserId) {
                    clearTimeout(timeoutHandle);
                    this.onClaimMasterShareEvent.remove(onClaim);
                    resolve(true);
                }
            };

            const timeoutHandle = window.setTimeout(() => {
                this.onClaimMasterShareEvent.remove(onClaim);
                resolve(false);
            }, timeoutMs);

            this.onClaimMasterShareEvent.add(onClaim);
            client.sendViaWs({
                type: Events.PLAYER_CLAIMING_MASTER_SHARE_ROLE,
                data: [],
            });
        });
    }

    async startMediasoupBroadcast(stream: MediaStream) {
        if (this.mediasoupUnavailableOnServer) {
            Logger.warn(MODULE, 'Server-based broadcast unavailable: mediasoup is disabled on the server');
            this.sharingVideo = false;
            return;
        }

        const mediasoup = this.ensureMediasoupClient();
        if (!mediasoup) {
            Logger.warn(MODULE, 'Mediasoup client not available; cannot start server-based broadcast');
            return;
        }

        const client = networkManager.client as CustomClient | undefined;

        try {
            const approved = await this.requestMasterShareApproval();
            if (!approved) {
                this.sharingVideo = false;
                Logger.warn(MODULE, 'Master share approval not received from server; aborting broadcast start');
                return;
            }

            client?.setBroadcastShareAudioGain(this.broadcastShareAudioMixGain);
            client?.setBroadcastShareAudioStream(stream);
            mediasoup.setSharerId(this.playerUserId);
            await mediasoup.startBroadcast(stream);
            this.sharingVideo = true;
            Logger.info(MODULE, 'Mediasoup broadcast started');
        } catch (err) {
            client?.setBroadcastShareAudioStream(null);
            this.sharingVideo = false;
            Logger.warn(MODULE, 'Mediasoup broadcast failed', err);
        }
    }

    async stopMediasoupBroadcast() {
        const client = networkManager.client as CustomClient | undefined;

        if (!this.mediasoupClient) {
            client?.setBroadcastShareAudioStream(null);
            this.sharingVideo = false;
            return;
        }

        try {
            await this.mediasoupClient.stopBroadcast();
        } catch (err) {
            Logger.warn(MODULE, 'Failed to stop mediasoup broadcast cleanly', err);
        } finally {
            client?.setBroadcastShareAudioStream(null);
        }

        networkManager.client?.sendViaWs({
            type: Events.PLAYER_RELEASING_MASTER_SHARE_ROLE,
            data: [],
        });

        this.sharingVideo = false;
        this.onShareStopEvent.notify(this.playerUserId);
    }

    async startMediasoupReceiving() {
        const mediasoup = this.ensureMediasoupClient();
        if (!mediasoup) {
            return;
        }

        try {
            await mediasoup.startReceiving();
            Logger.info(MODULE, 'Mediasoup receiving ready');
        } catch (err) {
            if (this.isMediasoupDisabledError(err)) {
                this.mediasoupUnavailableOnServer = true;
                if (!this.mediasoupUnavailableLogged) {
                    this.mediasoupUnavailableLogged = true;
                    Logger.info(MODULE, 'Mediasoup is disabled on this server; skipping mediasoup receive init');
                }
                return;
            }

            Logger.warn(MODULE, 'Mediasoup receiving init failed', err);
        }
    }

    private isMediasoupDisabledError(err: unknown): boolean {
        if (err instanceof Error) {
            return err.message.toLowerCase().includes('mediasoup not enabled');
        }
        if (typeof err === 'string') {
            return err.toLowerCase().includes('mediasoup not enabled');
        }
        return false;
    }

    private handleMediasoupVideoTrack(stream: MediaStream, track: MediaStreamTrack) {
        const senderId = Number((stream as any)._sharerId || 0);
        this.activeVideoStreams.set(senderId, stream);
        this.onShareEvent.notify(senderId, ({ track, streams: [stream] } as unknown) as RTCTrackEvent);
    }

    private handleMediasoupAudioTrack(stream: MediaStream, _track: MediaStreamTrack) {
        if (!this.mediasoupAudioElement) {
            this.mediasoupAudioElement = document.createElement('audio');
            this.mediasoupAudioElement.autoplay = true;
            this.mediasoupAudioElement.setAttribute('playsinline', '');
            document.body.appendChild(this.mediasoupAudioElement);
        }
        this.mediasoupAudioElement.srcObject = stream;
        this.mediasoupAudioElement.play().catch(() => undefined);
    }

    setBroadcastShareAudioMixGain(gain: number) {
        const clampedGain = Number.isFinite(gain) ? Math.max(0, Math.min(2, gain)) : 1;
        this.broadcastShareAudioMixGain = clampedGain;
        const client = networkManager.client as CustomClient | undefined;
        client?.setBroadcastShareAudioGain(clampedGain);
        SharedMediaController.instance.setLocalShareAudioGain(clampedGain);
    }

    getBroadcastShareAudioMixGain() {
        return this.broadcastShareAudioMixGain;
    }

    private handleMediasoupProducerClosed(_producerId: string, sharerId: number, kind: string | null = null) {
        Logger.debug(MODULE, `Mediasoup producer closed: sharerId=${sharerId}, kind=${kind ?? 'unknown'}, master=${this.masterSharerId}`);

        if (kind && kind !== 'video') {
            return;
        }

        if (sharerId > 0) {
            this.activeVideoStreams.delete(sharerId);
            this.onShareStopEvent.notify(sharerId);
            
            // If this was the master sharer, clear it and notify listeners
            if (this.masterSharerId === sharerId) {
                this.masterSharerId = 0;
                Logger.debug(MODULE, `Master sharer ${sharerId} stopped broadcasting`);
            }
        }
    }

    public get activePlayerObject(): Object3D | null {
        return this.engine.xr && this.engine.xr.session ? this.vrPlayerObject : this.playerObject;
    }

    public get activeRightHandObject() : Object3D | null {
        return this.engine.xr && this.engine.xr.session ? this.vrControllerRight : this.nonvrRightHand;
    }

    public get activeLeftHandObject() : Object3D | null {
        return this.engine.xr && this.engine.xr.session ? this.vrControllerLeft : this.nonvrLeftHand;
    }

    // Track event listeners for cleanup
    private eventListeners: Array<{
        element: HTMLElement | RTCPeerConnection;
        event: string;
        handler: EventListener | ((this: RTCPeerConnection, ev: RTCTrackEvent) => any);
    }> = [];

    static onRegister(engine: WonderlandEngine): void {
        const registerIfMissing = (componentType: any) => {
            if (!engine.isRegistered(componentType)) {
                engine.registerComponent(componentType);
            }
        };

        registerIfMissing(CopyRotation);
        registerIfMissing(CopyHeadNod);
        registerIfMissing(CopyPosition);
        registerIfMissing(VrBodyFollow);
        registerIfMissing(RiggedAvatarFollow);
        registerIfMissing(NetworkedComponent);
        registerIfMissing(NetworkedGrabbable);
        registerIfMissing(Orchestrated);

        // Components that may only exist in externally loaded room prefabs.
        registerIfMissing(NetworkListenVideoTexture);
    }

    // Singleton
    private static _instance: EventsClient;
    static get instance(): EventsClient {
        return EventsClient._instance;
    }

    static get instanceOrNull(): EventsClient | null {
        return EventsClient._instance ?? null;
    }

    /**
     * Add an event listener to an HTML element and track it for cleanup.
     */
    private addTrackedEventListener(
        element: HTMLElement,
        event: string,
        handler: EventListener
    ): void {
        element.addEventListener(event, handler);
        this.eventListeners.push({ element, event, handler });
    }

    private removeTrackedModalEventListeners(): void {
        const remaining: typeof this.eventListeners = [];
        for (const entry of this.eventListeners) {
            if (entry.element instanceof HTMLElement) {
                entry.element.removeEventListener(entry.event, entry.handler as EventListener);
                continue;
            }
            remaining.push(entry);
        }
        this.eventListeners = remaining;
    }

    /**
     * Add an event listener to an RTCPeerConnection and track it for cleanup.
     */
    addTrackedRTCEventListener(
        element: RTCPeerConnection,
        event: 'track',
        handler: (this: RTCPeerConnection, ev: RTCTrackEvent) => any
    ): void {
        element.addEventListener(event, handler);
        this.eventListeners.push({ element, event, handler });
    }

    /**
     * Remove all tracked event listeners.
     */
    private removeAllEventListeners(): void {
        console.log(`Removing ${this.eventListeners.length} tracked event listeners`);
        for (const { element, event, handler } of this.eventListeners) {
            element.removeEventListener(event, handler as EventListener);
        }
        this.eventListeners = [];
        
        // Remove the network event listener if it was registered
        if (this.boundOnEvent && networkManager) {
            console.log('Removing bound onEvent listener');
            networkManager.onEvent.remove(this.boundOnEvent);
            this.boundOnEvent = null;
        }
    }

    init() {
        configureLogger();

        //Initialize singleton
        if (EventsClient._instance) {
            Logger.error('There can only be one instance of EventsClient Component', MODULE);
        } else {
            EventsClient._instance = this;
        }

        UserMessaging.instance.registerEventsClient(this);
        HapticInfluenceDisplay.instance.registerEventsClient(this);

        // Apply shared styles once for the application
        applySharedStyles();
        
        // Apply component-specific styles
        const style = document.createElement('style');
        style.innerText = CSS;
        document.head.append(style);

        this.agemodal = createAgeModalContainer();
        document.body.appendChild(this.agemodal);

        this.usermodal = createUserModalContainer();
        document.body.appendChild(this.usermodal);
    } 

    override start() {
        if (EventsClient._instance == this) {
            this.onShareEvent.add(this.onShareTrackForAudio);
            this.onShareStopEvent.add(this.onShareStopForAudio);

            SharedMediaController.instance.attach();

            if (this.notificationsObject) {
                this.notificationText = this.notificationsObject.getComponent(TextComponent);
            } else {
                Logger.warn('notificationsObject is missing; notification text disabled', MODULE);
                this.notificationText = null;
            }

            if (this.hapticDisplayObject) {
                this.hapticDisplayText = this.hapticDisplayObject.getComponent(TextComponent);
                const hapticTextPositioner = this.hapticDisplayObject.getComponent(NotificationTextPositioner);
                if (hapticTextPositioner) {
                    hapticTextPositioner.placeTopRight = false;
                    hapticTextPositioner.placeTopLeft = true;
                    hapticTextPositioner.baseX = -0.95;
                    hapticTextPositioner.baseY = 0.6;
                    this.refreshHapticHudPositionerForMode();
                } else {
                    Logger.warn('hapticDisplayObject is missing NotificationTextPositioner; attach it in the editor to enable dynamic HUD positioning', MODULE);
                }
                this.hapticDisplayObject.active = this.hapticDisplayEnabled;
            } else {
                Logger.warn('hapticDisplayObject is missing; haptic display text disabled', MODULE);
                this.hapticDisplayText = null;
            }

            this.username = UserSettingsStore.getCookie(UserSettingsStore.Keys.EventsUsername);
            this.avatarUrl = UserSettingsStore.getCookie(UserSettingsStore.Keys.EventsAvatar);
            this.selectedAvatarId = this.normalizeAvatarId(
                UserSettingsStore.getCookie(UserSettingsStore.Keys.EventsAvatarId)
            );
            const roomCookie = UserSettingsStore.getCookie(UserSettingsStore.Keys.EventsRoom);
            if (roomCookie !== null) {
                this.room = roomCookie;
            }
            let cookieValue = UserSettingsStore.getCookie(UserSettingsStore.Keys.EventsShareMic);
            if (cookieValue !== null) {
                this.sharemic = (cookieValue === "true");
            }
            cookieValue = UserSettingsStore.getCookie(UserSettingsStore.Keys.EventsHeadlessMode);
            if (cookieValue !== null) {
                this.headlessmode = (cookieValue === "true");
            }

            this.applyLocalProstheticState({ type: 'none', networkId: null });

            /*
            if (this.username === '') {
            */
            this.openAgeVerificationModal();
            /*
            } else {
                this.connect();
            }
            */
        }
    }

    /** Instantiate or update a dynamic toy using the provided definition. */
    public registerToy(toy: EToyDefinition) {
        registerToyManager(this, toy);
    }

    /** Remove a single toy instance and associated state. */
    public removeToy(networkId: number) {
        removeToyManager(this, networkId);
    }

    /** Remove all toys; optionally only a provided set of ids. */
    public removeAllToys(ids?: number[]) {
        removeAllToysManager(this, ids);
    }

    private normalizeProstheticType(value: unknown): ProstheticType {
        const raw = String(value ?? 'none').toLowerCase();
        if (raw === 'inny' || raw === 'outy') {
            return raw;
        }
        return 'none';
    }

    private normalizeAvatarId(value: unknown): string {
        return normalizeCatalogAvatarId(value);
    }

    public getSelectedAvatarId(): string {
        return this.selectedAvatarId;
    }

    public applyAvatarState(state: AvatarState): void {
        const nextAvatarId = this.normalizeAvatarId(state?.avatarId);
        this.selectedAvatarId = nextAvatarId;
        UserSettingsStore.setCookie(UserSettingsStore.Keys.EventsAvatarId, nextAvatarId, 365);
    }

    public getLocalProstheticState(): ProstheticState {
        return { ...this.localProstheticState };
    }

    private getConfiguredLocalProstheticObject(type: ProstheticType): Object3D | null {
        if (type === 'none') {
            return null;
        }

        return type === 'inny' ? this.localProstheticInny ?? null : this.localProstheticOuty ?? null;
    }

    private getAllConfiguredLocalProsthetics(): Object3D[] {
        return [
            this.localProstheticInny,
            this.localProstheticOuty,
        ].filter((obj): obj is Object3D => Boolean(obj));
    }

    private getRemoteProstheticTemplate(type: 'inny' | 'outy'): Object3D | null {
        const template = type === 'inny' ? this.remoteProstheticInnyTemplate : this.remoteProstheticOutyTemplate;
        if (!template) {
            Logger.warn(`Remote prosthetic template not configured for type=${type}`, MODULE);
            return null;
        }
        return template;
    }

    private getRemoteAvatarTemplate(avatarId: string): Object3D | null {
        const templateRoot = this.avatarTemplates;
        if (!templateRoot || templateRoot.isDestroyed) {
            Logger.warn('avatarTemplates root is missing; cannot resolve avatar template', MODULE);
            return null;
        }

        const avatarEntry = getAvatarEntryById(avatarId);
        const template = templateRoot.findByNameRecursive(avatarEntry.templateName)[0] ?? null;
        if (template) {
            return template;
        }

        Logger.warn(`Avatar template not found: ${avatarEntry.templateName}`, MODULE);

        const fallbackEntry = getAvatarEntryById(undefined);
        if (fallbackEntry.templateName === avatarEntry.templateName) {
            return null;
        }

        const fallbackTemplate = templateRoot.findByNameRecursive(fallbackEntry.templateName)[0] ?? null;
        if (!fallbackTemplate) {
            Logger.warn(`Fallback avatar template not found: ${fallbackEntry.templateName}`, MODULE);
        }
        return fallbackTemplate;
    }

    private findRemoteAvatarModsMount(remoteRoot: Object3D): Object3D | null {
        const candidates = remoteRoot.findByNameRecursive('RemoteAvatarMods');
        return candidates[0] ?? null;
    }

    /**
     * Resolved while the shell has no avatar attached, so identically named nodes inside an
     * avatar template cannot shadow them.
     */
    private resolveRemoteHeadAttachments(remoteUser: any, remoteRoot: Object3D): Object3D[] {
        const cached = remoteUser['headAttachments'] as Object3D[] | undefined;
        if (cached) {
            const alive = cached.filter((obj) => obj && !obj.isDestroyed);
            if (alive.length > 0) {
                remoteUser['headAttachments'] = alive;
                return alive;
            }
        }

        // These own the look-at-player component; their children (Username, Indicator) follow them.
        const attachmentNames = ['Name', 'ShowoffScreen'];
        const resolved: Object3D[] = [];
        for (const name of attachmentNames) {
            const attachment = remoteRoot.findByNameRecursive(name)[0];
            if (attachment) {
                resolved.push(attachment);
            }
        }

        remoteUser['headAttachments'] = resolved;
        return resolved;
    }

    private detachRemoteHeadAttachments(remoteUser: any, remoteRoot: Object3D): void {
        for (const attachment of this.resolveRemoteHeadAttachments(remoteUser, remoteRoot)) {
            if (attachment.parent !== remoteRoot) {
                attachment.parent = remoteRoot;
            }
        }
    }

    private readAnchorNetworkId(anchor: any): number {
        return typeof anchor?.id === 'number' && anchor.id >= 0 ? anchor.id : -1;
    }

    /** Re-attaches a networked anchor to the freshly cloned avatar so it keeps receiving transforms. */
    private bindRemoteAvatarAnchor(
        remoteUser: any,
        key: 'head' | 'leftHand' | 'rightHand',
        object: Object3D | null,
        networkId: number,
        isHand: boolean,
        inXR: boolean,
    ): void {
        if (!object || object.isDestroyed) {
            delete remoteUser[key];
            return;
        }

        // At join the id is still unknown; handleUserJoined performs the initial component setup.
        if (networkId < 0) {
            remoteUser[key] = { object, id: -1 };
            return;
        }

        object.getComponent(NetworkedComponent)?.destroy();
        object.addComponent(NetworkedComponent, {
            networkId,
            mode: 'receive',
        });

        if (isHand) {
            if (inXR) {
                object.addComponent(CopyRotation, {
                    axis: 3,
                    other: object,
                });
            }
            object.addComponent(CopyPosition, {
                other: object,
            });
        }

        remoteUser[key] = { object, id: networkId };
    }

    private attachRemoteHeadAttachments(remoteUser: any, remoteRoot: Object3D, head: Object3D): void {
        if (!head || head.isDestroyed) {
            Logger.warn('Could not find Head object while attaching remote head-linked UI objects', MODULE);
            return;
        }

        for (const attachment of this.resolveRemoteHeadAttachments(remoteUser, remoteRoot)) {
            if (attachment === head) {
                continue;
            }

            if (attachment.parent !== head) {
                attachment.parent = head;
            }
        }
    }

    private destroyRemoteAvatarVisual(userId: number): void {
        const remoteEntry = this.resolveRemoteUserEntry(userId);
        if (!remoteEntry) {
            return;
        }

        const remoteUser = remoteEntry.value;
        const remoteRoot = remoteUser?.root?.object as Object3D | undefined;
        if (remoteRoot && !remoteRoot.isDestroyed) {
            // Pull head-linked UI objects out of the outgoing avatar tree before destroying it.
            this.detachRemoteHeadAttachments(remoteUser, remoteRoot);
        }

        const avatarVisual = remoteUser['avatarVisual'];
        if (!avatarVisual) {
            return;
        }

        if (avatarVisual.object && !avatarVisual.object.isDestroyed) {
            avatarVisual.object.destroy();
        }

        delete remoteUser['avatarVisual'];
    }

    public applyRemoteAvatarState(userId: number, state: AvatarState): void {
        const avatarId = this.normalizeAvatarId(state?.avatarId);
        const remoteEntry = this.resolveRemoteUserEntry(userId);
        if (!remoteEntry) {
            return;
        }

        const remoteUser = remoteEntry.value;
        const remoteRoot = remoteUser?.root?.object as Object3D | undefined;
        if (!remoteRoot || remoteRoot.isDestroyed) {
            return;
        }

        const avatarMount = this.findRemoteAvatarModsMount(remoteRoot);
        if (!avatarMount) {
            Logger.warn(`Remote avatar mount "RemoteAvatarMods" not found for user ${userId}`, MODULE);
            return;
        }

        const template = this.getRemoteAvatarTemplate(avatarId);
        if (!template) {
            return;
        }

        // Captured before the old visual is destroyed: the outgoing anchors own these ids.
        const headNetworkId = this.readAnchorNetworkId(remoteUser['head']);
        const leftHandNetworkId = this.readAnchorNetworkId(remoteUser['leftHand']);
        const rightHandNetworkId = this.readAnchorNetworkId(remoteUser['rightHand']);
        const inXR = Boolean(this.remoteUserJoinData.get(userId)?.inXR);
        const previousPoses = new Map<string, Float32Array>();
        for (const key of ['head', 'leftHand', 'rightHand']) {
            const object = remoteUser[key]?.object as Object3D | undefined;
            if (object && !object.isDestroyed) {
                previousPoses.set(key, object.getTransformWorld(new Float32Array(8)));
            }
        }

        this.destroyRemoteAvatarVisual(userId);

        // The wrapper owns both the visual and independent network targets, so
        // swaps/user-leave destroy all of them together without touching the shell.
        const rigged = resolveAvatarTrackingObjects(template).rigged;
        const wrapper = rigged ? avatarMount.scene.addObject(avatarMount) : null;
        if (wrapper) wrapper.name = 'RemoteRiggedAvatar';
        const instance = template.clone(wrapper ?? avatarMount);
        if (!instance) {
            wrapper?.destroy();
            Logger.warn(`Failed to clone avatar template for avatarId=${avatarId}`, MODULE);
            return;
        }

        this.removeDeactivateOnStartComponents(instance);
        setComponentsActive(instance, true);

        const bones = resolveAvatarTrackingObjects(instance);
        let anchors = bones;
        if (wrapper) {
            const createTarget = (name: string, bone: Object3D): Object3D => {
                const target = wrapper.scene.addObject(wrapper);
                target.name = name;
                target.setTransformWorld(bone.getTransformWorld(new Float32Array(8)));
                return target;
            };
            anchors = {
                head: createTarget('Head', bones.head),
                leftHand: createTarget('LeftHand', bones.leftHand!),
                rightHand: createTarget('RightHand', bones.rightHand!),
                rigged: true,
            };
            instance.addComponent(RiggedAvatarFollow).initialize(bones, anchors);
        }
        for (const [key, object] of [
            ['head', anchors.head],
            ['leftHand', anchors.leftHand],
            ['rightHand', anchors.rightHand],
        ] as const) {
            const pose = previousPoses.get(key);
            if (object && pose) object.setTransformWorld(pose);
        }

        const headObject = anchors.head;
        this.attachRemoteHeadAttachments(remoteUser, remoteRoot, headObject);

        remoteUser['avatarVisual'] = {
            object: wrapper ?? instance,
            id: -1,
            avatarId,
        };

        this.bindRemoteAvatarAnchor(remoteUser, 'head', headObject, headNetworkId, false, inXR);
        this.bindRemoteAvatarAnchor(
            remoteUser,
            'leftHand',
            anchors.leftHand,
            leftHandNetworkId,
            true,
            inXR,
        );
        this.bindRemoteAvatarAnchor(
            remoteUser,
            'rightHand',
            anchors.rightHand,
            rightHandNetworkId,
            true,
            inXR,
        );
    }

    private removeDeactivateOnStartComponents(root: Object3D): void {
        for (const component of root.getComponents()) {
            if ((component as any).type === 'deactivate-on-start') {
                component.destroy();
            }
        }

        for (const child of root.children) {
            this.removeDeactivateOnStartComponents(child);
        }
    }

    private resolveRemoteUserEntry(userId: number): { key: string | number; value: any } | null {
        for (const [key, value] of this.remoteUsers.entries()) {
            if (Number(key) === userId) {
                return { key, value };
            }
        }
        return null;
    }

    private destroyRemoteProstheticEntry(userId: number, networkId?: number | null): void {
        const remoteEntry = this.resolveRemoteUserEntry(userId);
        if (!remoteEntry) {
            return;
        }
        const remoteUser = remoteEntry.value;

        const current = remoteUser['prosthetic'];
        if (!current) {
            if (typeof networkId === 'number' && networkId >= 0) {
                networkManager.removeObject(networkId);
            }
            return;
        }

        if (typeof current.id === 'number') {
            networkManager.removeObject(current.id);
        }
        if (current.object && !current.object.isDestroyed) {
            current.object.destroy();
        }
        delete remoteUser['prosthetic'];

        if (typeof networkId === 'number' && networkId >= 0 && current.id !== networkId) {
            networkManager.removeObject(networkId);
        }
    }

    private deactivateAllLocalProstheticObjects(): void {
        for (const prostheticObj of this.getAllConfiguredLocalProsthetics()) {
            if (prostheticObj.isDestroyed) {
                continue;
            }
            setComponentsActive(prostheticObj, false);
        }
    }

    private removeLocalProstheticNetworkComponents(): void {
        for (const prostheticObj of this.getAllConfiguredLocalProsthetics()) {
            if (prostheticObj.isDestroyed) {
                continue;
            }

            const netComp = prostheticObj.getComponent(NetworkedComponent);
            if (netComp) {
                netComp.destroy();
            }
        }
    }

    private bindLocalProstheticNetworkComponent(): void {
        const state = this.localProstheticState;
        if (state.type === 'none' || state.networkId === null) {
            this.removeLocalProstheticNetworkComponents();
            return;
        }

        const activeObj = this.getConfiguredLocalProstheticObject(state.type);
        if (!activeObj) {
            Logger.warn(`Local prosthetic object missing for type=${state.type}`, MODULE);
            return;
        }

        const inactiveType: ProstheticType = state.type === 'inny' ? 'outy' : 'inny';
        const inactiveObj = this.getConfiguredLocalProstheticObject(inactiveType);
        if (inactiveObj && inactiveObj !== activeObj) {
            const old = inactiveObj.getComponent(NetworkedComponent);
            if (old) {
                old.destroy();
            }
        }

        const existing = activeObj.getComponent(NetworkedComponent);
        if (existing) {
            if (existing.networkId !== state.networkId) {
                existing.destroy();
                activeObj.addComponent(NetworkedComponent, {
                    networkId: state.networkId,
                    mode: 'send',
                });
            }
            return;
        }

        activeObj.addComponent(NetworkedComponent, {
            networkId: state.networkId,
            mode: 'send',
        });
    }

    public applyLocalProstheticState(state: ProstheticState): void {
        this.localProstheticState = {
            type: this.normalizeProstheticType(state.type),
            networkId: typeof state.networkId === 'number' ? state.networkId : null,
        };

        this.deactivateAllLocalProstheticObjects();
        if (this.localProstheticState.type !== 'none') {
            const obj = this.getConfiguredLocalProstheticObject(this.localProstheticState.type);
            if (obj) {
                setComponentsActive(obj, true);
            }
        }
        this.bindLocalProstheticNetworkComponent();
    }

    public applyRemoteProstheticState(userId: number, state: ProstheticState, deactivatedNetworkId?: number | null): void {
        const normalizedState: ProstheticState = {
            type: this.normalizeProstheticType(state.type),
            networkId: typeof state.networkId === 'number' ? state.networkId : null,
        };

        if (typeof deactivatedNetworkId === 'number') {
            this.destroyRemoteProstheticEntry(userId, deactivatedNetworkId);
        } else {
            this.destroyRemoteProstheticEntry(userId);
        }

        if (normalizedState.type === 'none' || normalizedState.networkId === null) {
            return;
        }

        const template = this.getRemoteProstheticTemplate(normalizedState.type);
        if (!template) {
            return;
        }

        const instance = template.clone(null);
        if (!instance) {
            Logger.warn(`Failed to clone remote prosthetic template for type=${normalizedState.type}`, MODULE);
            return;
        }

        // Templates can carry one-shot scene startup helpers that should not run on spawned clones.
        this.removeDeactivateOnStartComponents(instance);

        // Remote templates are kept hidden in-scene; cloned instances must be explicitly activated.
        setComponentsActive(instance, true);

        const netComp = instance.getComponent(NetworkedComponent);
        if (netComp) {
            netComp.destroy();
        }
        instance.addComponent(NetworkedComponent, {
            networkId: normalizedState.networkId,
            mode: 'receive',
        });

        const remoteEntry = this.resolveRemoteUserEntry(userId);
        if (!remoteEntry) {
            instance.destroy();
            return;
        }
        const remoteUser = remoteEntry.value;

        remoteUser['prosthetic'] = {
            object: instance,
            id: normalizedState.networkId,
        };
    }

    public requestProstheticSelection(type: ProstheticType): void {
        if (!networkManager?.client) {
            Logger.warn('Cannot request prosthetic update: not connected', MODULE);
            return;
        }

        const normalized = this.normalizeProstheticType(type);
        networkManager.client.sendViaWs({
            type: Events.PROSTHETIC_SET_REQUEST,
            data: {
                type: normalized,
            },
        });
    }

    public requestAvatarSelection(avatarId: string): void {
        const normalized = this.normalizeAvatarId(avatarId);

        if (!networkManager?.client) {
            Logger.warn('Cannot request avatar update: not connected', MODULE);
            this.applyAvatarState({ avatarId: normalized });
            return;
        }

        networkManager.client.sendViaWs({
            type: Events.AVATAR_SET_REQUEST,
            data: {
                avatarId: normalized,
            },
        });
    }

    /** Get a flattened forward vector (XZ plane) for a source object or default. */
    private getForwardXZ(source: Object3D | null | undefined): vec3 {
        const forward = vec3.create();
        if (source && typeof (source as any).getForward === 'function') {
            (source as any).getForwardWorld(forward);
        }
        // If forward is zero, try to derive from rotation (yaw) if available
        if (forward[0] === 0 && forward[1] === 0 && forward[2] === 0 && source) {
            const rot = quat.create();
            if (typeof (source as any).getRotationWorld === 'function') {
                (source as any).getRotationWorld(rot);
                vec3.set(forward, 0, 0, -1);
                vec3.transformQuat(forward, forward, rot);
            }
        }
        // Default forward if still zero-length
        if (forward[0] === 0 && forward[1] === 0 && forward[2] === 0) {
            vec3.set(forward, 0, 0, -1);
        }
        // Flatten to XZ
        forward[1] = 0;
        if (vec3.length(forward) < 1e-5) {
            vec3.set(forward, 0, 0, -1);
        }
        vec3.normalize(forward, forward);
        return forward;
    }

    public requestOrchestratedInfo(nametag: string, transform: number[]) {
        // If not connected yet, queue the request
        if (!this.isConnected) {
            Logger.info(
                `Queueing orchestrated object info request for nametag=${nametag} (connection not ready)`,
                MODULE
            );
            this.pendingOrchestratedRequests.push({ nametag, transform });
            return;
        }

        Logger.info(
            `Requesting orchestrated object info for nametag=${nametag}`,
            MODULE
        );
        
        networkManager.client?.sendViaWs({
            type: Events.ORCHESTRATED_INFO_REQUEST,
            data: { nametag, transform },
        });
    }

    /** Request the server to create a toy of the given type. */
    public requestToyCreate(type: EToyType) {
        if (!networkManager?.client) {
            Logger.warn('Cannot request toy creation: not connected', MODULE);
            return;
        }

        const posSource = this.activePlayerObject;
        const basePos = posSource ? posSource.getPositionWorld(tempVec) : vec3.fromValues(0, 1, 0);

        // Place within 1 unit and within +/-45 degrees of forward on XZ, with fixed Y for visibility
        const fwd = this.getForwardXZ(posSource);
        const yawOffset = (Math.random() * (Math.PI / 2)) - (Math.PI / 4); // -45..45 degrees
        const cosY = Math.cos(yawOffset);
        const sinY = Math.sin(yawOffset);
        const dirX = fwd[0] * cosY - fwd[2] * sinY;
        const dirZ = fwd[0] * sinY + fwd[2] * cosY;
        const radius = 0.5 + (Math.random() * 1.75); // 0.5..2.25 units
        const spawnX = basePos[0] + dirX * radius;
        const spawnZ = basePos[2] + dirZ * radius;
        const spawnY = 1; // fixed height for easier discovery

        // Encode translation into dual part: qd = 0.5 * t * qr (qr is identity)
        const transform = [0, 0, 0, 1, spawnX * 0.5, spawnY * 0.5, spawnZ * 0.5, 0];

        Logger.info(
            `Requesting toy create type=${type} from playerPos=(${basePos[0].toFixed(2)}, ${basePos[1].toFixed(2)}, ${basePos[2].toFixed(2)}) ` +
            `spawnPos=(${spawnX.toFixed(2)}, ${spawnY.toFixed(2)}, ${spawnZ.toFixed(2)})`,
            MODULE
        );

        networkManager.client.sendViaWs({
            type: Events.TOY_CREATE_REQUEST,
            data: { type, transform },
        });
    }

    /** Request server to delete all toys. */
    public requestToyDeleteAll() {
        if (!networkManager?.client) {
            Logger.warn('Cannot request toy delete all: not connected', MODULE);
            return;
        }
        networkManager.client.sendViaWs({
            type: Events.TOY_DELETE_ALL_REQUEST,
            data: {},
        });
    }

    public requestSceneSwitch(sceneRef: string) {
        if (!networkManager?.client) {
            Logger.warn('Cannot request scene switch: not connected', MODULE);
            return;
        }

        if (!sceneRef) {
            Logger.warn('Cannot request scene switch: empty sceneRef', MODULE);
            return;
        }

        Logger.info(`Requesting scene switch to: ${sceneRef}`, MODULE);

        networkManager.client.sendViaWs({
            type: Events.SCENE_SWITCH_REQUEST,
            data: { sceneRef },
        });
    }

    public requestSynchronizedUrlVideoStart(
        url: string,
        seekSeconds: number = 0,
        leadTimeMs: number = 3000,
        options?: {
            enableResyncPings?: boolean;
            pingIntervalMs?: number;
        }
    ) {
        if (!networkManager?.client) {
            Logger.warn('Cannot request synchronized URL video start: not connected', MODULE);
            return;
        }

        const normalizedUrl = typeof url === 'string' ? url.trim() : '';
        if (!normalizedUrl) {
            Logger.warn('Cannot request synchronized URL video start: empty URL', MODULE);
            return;
        }

        const normalizedSeek = Number.isFinite(seekSeconds) ? Math.max(0, seekSeconds) : 0;
        const normalizedLead = Number.isFinite(leadTimeMs)
            ? Math.max(1500, Math.min(15000, Math.round(leadTimeMs)))
            : 3000;
        const enableResyncPings = options?.enableResyncPings !== false;
        const pingIntervalMsRaw = Number(options?.pingIntervalMs);
        const pingIntervalMs = Number.isFinite(pingIntervalMsRaw)
            ? Math.max(2000, Math.min(15000, Math.round(pingIntervalMsRaw)))
            : 4000;

        networkManager.client.sendViaWs({
            type: Events.PLAYER_SYNC_URL_VIDEO_START,
            data: {
                url: normalizedUrl,
                seekSeconds: normalizedSeek,
                leadTimeMs: normalizedLead,
                enableResyncPings,
                pingIntervalMs,
            },
        });
    }

    public setSceneSwitchCooldownSeconds(seconds: number) {
        const normalized = Number.isFinite(seconds) ? Math.max(0, Math.ceil(seconds)) : 0;
        if (normalized <= 0) {
            this.sceneSwitchCooldownUntilMs = 0;
            return;
        }

        this.sceneSwitchCooldownUntilMs = Date.now() + normalized * 1000;
    }

    public clearSceneSwitchCooldown() {
        this.sceneSwitchCooldownUntilMs = 0;
    }

    public getSceneSwitchCooldownRemainingSeconds(): number {
        if (!this.sceneSwitchCooldownUntilMs) {
            return 0;
        }

        const remainingMs = this.sceneSwitchCooldownUntilMs - Date.now();
        if (remainingMs <= 0) {
            this.sceneSwitchCooldownUntilMs = 0;
            return 0;
        }

        return Math.ceil(remainingMs / 1000);
    }

    openAgeVerificationModal() {
        this.preAuthStage = 'age';
        this.preAuthShowLoginError = false;
        this.preAuthShowRoomPasswordError = false;
        this.preAuthRoomJoinError = '';
        UserMessaging.instance.clearMessages();
        this.notify('');
        this.refreshPreAuthPresentation();
    }

    openUsernameModal(showLoginError: boolean = false, showRoomPasswordError: boolean = false, showRoomJoinError?: string) {
        this.preAuthStage = 'login';
        this.preAuthShowLoginError = showLoginError;
        this.preAuthShowRoomPasswordError = showRoomPasswordError;
        this.preAuthRoomJoinError = showRoomJoinError ?? '';
        this.refreshPreAuthPresentation();
    }

    private hideHtmlPreAuthModals(): void {
        if (this.agemodal) {
            this.agemodal.style.display = 'none';
        }
        if (this.usermodal) {
            this.usermodal.style.display = 'none';
        }
    }

    private hidePreAuthPresentation(): void {
        this.preAuthStage = 'hidden';
        this.preAuthShowLoginError = false;
        this.preAuthShowRoomPasswordError = false;
        this.preAuthRoomJoinError = '';
        this.hideHtmlPreAuthModals();
        this.onPreAuthStateChanged.notify();
    }

    private recreateAgeModal(): void {
        this.removeTrackedModalEventListeners();
        this.agemodal?.remove();
        this.agemodal = createAgeModalContainer();
        document.body.appendChild(this.agemodal);
    }

    private recreateUserModal(): void {
        this.removeTrackedModalEventListeners();
        this.usermodal?.remove();
        this.usermodal = createUserModalContainer();
        document.body.appendChild(this.usermodal);
    }

    private refreshPreAuthPresentation(): void {
        const vrState = this.getVrPreAuthMenuState();
        if (!vrState) {
            this.hideHtmlPreAuthModals();
            this.onPreAuthStateChanged.notify();
            return;
        }

        if (this.engine.xr?.session) {
            this.hideHtmlPreAuthModals();
            this.onPreAuthStateChanged.notify();
            return;
        }

        this.hideHtmlPreAuthModals();
        if (vrState.stage === 'age') {
            this.recreateAgeModal();
            openAgeVerificationModalUi({
                ageModal: this.agemodal,
                userModal: this.usermodal,
                addTrackedEventListener: this.addTrackedEventListener.bind(this),
                onConfirm: () => {
                    this.confirmPreAuthAgeVerification();
                },
                onDeny: () => {
                    this.denyPreAuthAgeVerification();
                },
            });
        } else {
            this.recreateUserModal();
            openUsernameModalUi({
                userModal: this.usermodal,
                addTrackedEventListener: this.addTrackedEventListener.bind(this),
                username: this.username,
                roomName: this.room,
                sharemic: this.sharemic,
                headlessmode: this.headlessmode,
                showLoginError: this.preAuthShowLoginError,
                showRoomPasswordError: this.preAuthShowRoomPasswordError,
                showRoomJoinError: this.preAuthRoomJoinError,
                onSubmit: (values) => {
                    void this.submitPreAuthLogin(values);
                },
            });
        }

        this.onPreAuthStateChanged.notify();
    }

    /**
     * Authenticates the user against the REST server.
     * Sends credentials over HTTPS; stores the JWT in memory only — never in cookies or storage.
     * @throws on invalid credentials or network error
     */
    private async loginWithPassword(username: string, password: string): Promise<void> {
        const loginUrl = `${this.getRestApiBaseUrl()}/api/auth/login`;

        const response = await fetch(loginUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
            Logger.warn('Login failed', MODULE, { status: response.status });
            throw new Error('Login failed');
        }

        const data = (await response.json()) as { token: string; user: object };
        this.authToken = data.token;
        Logger.info('Login successful', MODULE);
    }

    /**
     * Parse incoming event data from WebSocket or custom events.
     * Safely extracts and deserializes JSON data with fallback null handling.
     * 
     * @param event - Event object (WebSocket MessageEvent or CustomEvent)
     * @returns Parsed JavaScript object, or null if invalid/missing data
     * 
     * @remarks
     * Data extraction strategy (priority order):
     * 1. event.data (WebSocket messages)
     * 2. event.detail (CustomEvents)
     * 3. null (fallback for malformed events)
     * 
     * Error handling:
     * - Returns null on JSON parse errors (invalid format)
     * - Returns null for null/undefined input
     * - Never throws exceptions (safe for use in event handlers)
     * 
     * Used for:
     * - Network event messages from server
     * - WebRTC signaling messages (offers, answers, ICE candidates)
     * - Custom application events
     */
    parse(event: any) {
        try {
            const raw = event && (event.data ?? event.detail ?? null);
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Initiates connection to the multiplayer server via networkManager.
     * Sends custom join data including username, avatar URL, and VR configuration.
     * 
     * @remarks
     * Custom join data sent to server:
     * - handTracking: false (hand tracking disabled for now)
     * - hands: true (hand rendering enabled)
     * - username: User's display name from cookie or prompt
     * - avatarUrl: Custom avatar URL or default
     * - headlessmode: Whether running without rendering (server/bot mode)
     * 
     * Connection options:
     * - host: Server hostname from serverHost property
     * - port: Server port from serverPort property
     * - Passed to networkManager.client.connect()
     * 
     * Connection flow:
     * 1. Checks if already connecting (prevents duplicate connections)
     * 2. Builds customJoinData with user info and VR config
     * 3. Calls networkManager.client.connect() with options
     * 4. Server receives join data and creates player object
     * 5. Player receives peer ID and begins synchronization
     * 
     * Note: Connection is asynchronous, success handled by network event listeners
     */
    connect() {
        UserMessaging.instance.notifyState('Connecting to server...');
        const customJoinData = {
            handTracking: false,
            hands: true,
            username: this.username,
            avatarUrl: this.avatarUrl,
            avatarId: this.selectedAvatarId,
            headlessmode: this.headlessmode,
            room: this.room,
            roomPassword: this.roomPassword,
        };
        const customJoinOptions = {
                host: this.serverHost,
                port: this.serverPort,
                secure: this.secure,
                audio: this.audio,
                audioDeviceId: this.inputDeviceId,
                debug: this.debug,
                path: this.serverPath,
        };
        if (this.connecting) {
            Logger.error('Cannot connect multiple times', MODULE);
            return;
        } else {
            this.connecting = true;
        }
        if(this.headlessmode) {
            Logger.info("Connecting in headless mode -- disabling audio and avatar", MODULE);
            customJoinData.avatarUrl = "";
            customJoinData.hands = false;
            customJoinOptions.audio = false;
            this.audio = false;
        }
        Logger.info('Connecting to server', MODULE, {
            host: this.serverHost,
            port: this.serverPort,
            secure: this.secure,
            audio: this.audio,
            audioDeviceId: this.inputDeviceId,
            debug: this.debug,
            path: this.serverPath,
        });

        let cusClient = new CustomClient(customJoinOptions);
        cusClient.sharemicrophone = this.sharemic;
        cusClient.headlessmode = this.headlessmode;
        cusClient.authToken = this.authToken;
        networkManager.client = cusClient;

        // Register onEvent handler NOW, before connect(), so no server messages are dropped.
        // onDeactivate (triggered by this.active = false in switchRoom) may have removed it.
        if (!this.boundOnEvent) {
            this.boundOnEvent = this.onEvent.bind(this);
            networkManager.onEvent.add(this.boundOnEvent);
        }

        networkManager
            .connect(customJoinData, customJoinOptions)
            .then(this.onSuccessfulConnection.bind(this))
            .catch((e) => {
                if (e?.message === 'ROOM_PASSWORD_INCORRECT') {
                    Logger.warn('Room password incorrect', MODULE);
                    this.active = false;
                    this.connecting = false;
                    this.openUsernameModal(false, true);
                    return;
                }
                if (e?.message === 'ROOM_FULL') {
                    Logger.warn('Room is full', MODULE);
                    this.active = false;
                    this.connecting = false;
                    this.openUsernameModal(false, false, 'This room is full. Please try a different room.');
                    return;
                }
                if (e?.message === 'ROOM_LIMIT_REACHED') {
                    Logger.warn('Server room limit reached', MODULE);
                    this.active = false;
                    this.connecting = false;
                    this.openUsernameModal(false, false, 'The server has reached its room limit. Please try again later.');
                    return;
                }
                if (e?.message === 'ROOM_CREATE_COOLDOWN') {
                    Logger.warn('Room creation cooldown active', MODULE);
                    this.active = false;
                    this.connecting = false;
                    this.openUsernameModal(false, false, 'You recently created a room. Please wait before creating another.');
                    return;
                }
                Logger.error('Connection failed', MODULE, e);
                UserMessaging.instance.notifyState('Connection failed. Retrying...');
                /* No more updates for now. */
                this.active = false;
                this.connecting = false;
                /* Automatically reconnect -- for debugging the server */
                setTimeout(this.connect.bind(this), 1000);
            });
    }

    update(dt: number) {
        // Timeout management now handled by UserMessaging
        HapticInfluenceDisplay.instance.tick();
        if (this.debugAvatarTracking) {
            this.avatarTrackingDebugSeconds += dt;
            if (this.avatarTrackingDebugSeconds >= 2) {
                this.avatarTrackingDebugSeconds = 0;
                this.logAvatarTrackingDiagnostics();
            }
        } else {
            this.avatarTrackingDebugSeconds = 0;
        }
    }

    public logAvatarTrackingDiagnostics(): void {
        const world = (object: Object3D | null | undefined) =>
            object && !object.isDestroyed ? Array.from(object.getPositionWorld(vec3.create())) : null;
        const trackingOrigin = this.vrPlayerObject?.parent;
        const configuredHeight = (trackingOrigin?.getComponent('player-height') as any)?.height ?? null;
        const remotes = [];
        for (const [userId, user] of this.remoteUsers) {
            const visual = user?.avatarVisual?.object as Object3D | undefined;
            if (!visual || visual.isDestroyed) continue;
            const follow = visual.getComponent(RiggedAvatarFollow)
                ?? visual.children.map((child) => child.getComponent(RiggedAvatarFollow)).find(Boolean);
            if (!follow) continue;
            remotes.push({
                userId,
                inXR: Boolean(this.remoteUserJoinData.get(Number(userId))?.inXR),
                avatarId: (user.avatarVisual as { avatarId?: string }).avatarId,
                tracking: follow.getTrackingDiagnostics(),
            });
        }
        // JSON captures numeric values now, unlike lazy console object inspection.
        console.info('[AvatarTracking]', JSON.stringify({
            local: {
                userId: this.playerUserId,
                inXR: Boolean(this.engine.xr?.session),
                referenceSpace: this.engine.xr?.currentReferenceSpaceType ?? null,
                configuredHeight,
                playerWorld: world(this.object),
                trackingOriginWorld: world(trackingOrigin),
                desktopHeadWorld: world(this.playerObject),
                xrHeadWorld: world(this.vrPlayerObject),
                sentHeadWorld: world(this.activePlayerObject),
                sentLeftWorld: world(this.activeLeftHandObject),
                sentRightWorld: world(this.activeRightHandObject),
            },
            remotes,
        }));
    }

    onActivate(): void {
        this.engine.onXRSessionStart.add(this.enterVR);
        this.engine.onXRSessionEnd.add(this.exitVR);
    }

    onDeactivate(): void {
        this.removeAllEventListeners();
        this.onShareEvent.remove(this.onShareTrackForAudio);
        this.onShareStopEvent.remove(this.onShareStopForAudio);
        this.removeAllP2PAudioElements();
        this.engine.onXRSessionStart.remove(this.enterVR);
        this.engine.onXRSessionEnd.remove(this.exitVR);
    }

    private refreshHapticHudPositionerForMode(): void {
        if (!this.hapticDisplayObject) {
            return;
        }

        const hapticTextPositioner = this.hapticDisplayObject.getComponent(NotificationTextPositioner);
        if (!hapticTextPositioner) {
            return;
        }

        hapticTextPositioner.refreshPosition();
    }

    private handleP2PShareTrackForAudio(senderId: number, e: RTCTrackEvent) {
        if (senderId <= 0 || senderId === this.playerUserId) {
            return;
        }

        const stream = e.streams?.[0] || (e.track ? new MediaStream([e.track]) : null);
        if (!stream) {
            return;
        }

        // Some browsers deliver video track first and add audio to the same stream later.
        // Listen for late-added audio tracks and bind playback as soon as one appears.
        this.attachLateAudioTrackListener(senderId, stream);

        const hasAudio = stream.getAudioTracks().some((track) => track.readyState === 'live');
        if (!hasAudio) {
            return;
        }

        let audioElement = this.p2pRemoteAudioElements.get(senderId);
        if (!audioElement) {
            audioElement = document.createElement('audio');
            audioElement.autoplay = true;
            audioElement.controls = false;
            audioElement.muted = false;
            audioElement.volume = 1.0;
            audioElement.style.display = 'none';
            audioElement.setAttribute('playsinline', '');
            document.body.appendChild(audioElement);
            this.p2pRemoteAudioElements.set(senderId, audioElement);
        }

        if (audioElement.srcObject !== stream) {
            audioElement.srcObject = stream;
        }

        audioElement.play().catch((err) => {
            Logger.warn(MODULE, `Failed to autoplay P2P shared audio for sender ${senderId}`, err);
        });
    }

    private attachLateAudioTrackListener(senderId: number, stream: MediaStream) {
        const existing = this.p2pRemoteAudioTrackListeners.get(senderId);
        if (existing && existing.stream === stream) {
            return;
        }

        if (existing) {
            existing.stream.removeEventListener('addtrack', existing.handler);
            this.p2pRemoteAudioTrackListeners.delete(senderId);
        }

        const handler = (event: MediaStreamTrackEvent) => {
            if (event.track.kind !== 'audio') {
                return;
            }

            this.handleP2PShareTrackForAudio(senderId, {
                track: event.track,
                streams: [stream],
            } as unknown as RTCTrackEvent);
        };

        stream.addEventListener('addtrack', handler);
        this.p2pRemoteAudioTrackListeners.set(senderId, { stream, handler });
    }

    private removeP2PAudioTrackListener(senderId: number) {
        const existing = this.p2pRemoteAudioTrackListeners.get(senderId);
        if (!existing) {
            return;
        }

        existing.stream.removeEventListener('addtrack', existing.handler);
        this.p2pRemoteAudioTrackListeners.delete(senderId);
    }

    private removeP2PAudioElement(senderId: number) {
        this.removeP2PAudioTrackListener(senderId);

        const audioElement = this.p2pRemoteAudioElements.get(senderId);
        if (!audioElement) {
            return;
        }

        audioElement.pause();
        audioElement.srcObject = null;
        audioElement.remove();
        this.p2pRemoteAudioElements.delete(senderId);
    }

    private removeAllP2PAudioElements() {
        for (const senderId of this.p2pRemoteAudioTrackListeners.keys()) {
            this.removeP2PAudioTrackListener(senderId);
        }
        for (const senderId of this.p2pRemoteAudioElements.keys()) {
            this.removeP2PAudioElement(senderId);
        }
    }

    enterVR = () => {
        this.refreshHapticHudPositionerForMode();
        this.refreshPreAuthPresentation();

        if (networkManager && networkManager.client) {
            networkManager.client.sendViaWs({
                type: Events.USER_ENTER_XR,
                data: {},
            });
        }

        if (!this.playerObject) return;
        
        this.playerObject.active = false;
        let networkComponent = this.playerObject.getComponent(NetworkedComponent);
        if (networkComponent) {
            networkComponent.destroy();
        }
        let leftHandId = 1; //this will be overwritten
        let rightHandId = 2; //this will be overwritten
        networkComponent = this.nonvrLeftHand.getComponent(NetworkedComponent);
        if (networkComponent) {
            leftHandId = networkComponent.networkId;
            networkComponent.destroy();
        }
        networkComponent = this.nonvrRightHand.getComponent(NetworkedComponent);
        if (networkComponent) {
            rightHandId = networkComponent.networkId;
            networkComponent.destroy();
        }

        if (!this.vrPlayerObject.getComponent(NetworkedComponent)) {
            this.vrPlayerObject.addComponent(NetworkedComponent, {
                networkId: this.playerNetworkId,
                mode: 'send',
            });
        }
        if (!this.vrControllerLeft.getComponent(NetworkedComponent)) {
            this.vrControllerLeft.addComponent(NetworkedComponent, {
                networkId: leftHandId,
                mode: 'send',
            });
        }
        if (!this.vrControllerRight.getComponent(NetworkedComponent)) {
            this.vrControllerRight.addComponent(NetworkedComponent, {
                networkId: rightHandId,
                mode: 'send',
            });
        }

        this.vrPlayerObject!.active = true;
        this.bindLocalProstheticNetworkComponent();
    };

    exitVR = () => {
        this.refreshHapticHudPositionerForMode();
        this.refreshPreAuthPresentation();

        if (networkManager && networkManager.client) {
            networkManager.client.sendViaWs({
                type: Events.USER_EXIT_XR,
                data: {},
            });
        }
        
        if (!this.vrPlayerObject || !this.playerObject) return;
        
        this.vrPlayerObject.active = false;
        let networkComponent = this.vrPlayerObject.getComponent(NetworkedComponent);
        if (networkComponent) {
            networkComponent.destroy();
        }
        let leftHandId = 1; //this will be overwritten
        let rightHandId = 2; //this will be overwritten
        networkComponent = this.vrControllerLeft.getComponent(NetworkedComponent);
        if (networkComponent) {
            leftHandId = networkComponent.networkId;
            networkComponent.destroy();
        }
        networkComponent = this.vrControllerRight.getComponent(NetworkedComponent);
        if (networkComponent) {
            rightHandId = networkComponent.networkId;
            networkComponent.destroy();
        }

        if (!this.playerObject.getComponent(NetworkedComponent)) {
            this.playerObject.addComponent(NetworkedComponent, {
                networkId: this.playerNetworkId,
                mode: 'send',
            });
        }
        if (!this.nonvrLeftHand.getComponent(NetworkedComponent)) {
            this.nonvrLeftHand.addComponent(NetworkedComponent, {
                networkId: leftHandId,
                mode: 'send',
            });
        }
        if (!this.nonvrRightHand.getComponent(NetworkedComponent)) {
            this.nonvrRightHand.addComponent(NetworkedComponent, {
                networkId: rightHandId,
                mode: 'send',
            });
        }
        this.playerObject.active = true;
        this.bindLocalProstheticNetworkComponent();
    };    
    
    override async onSuccessfulConnection(joinEvent: number[]) {
        try {
            // Fire any one-shot connection callback (e.g. from switchRoom)
            if (this._onNextConnection) {
                const cb = this._onNextConnection;
                this._onNextConnection = null;
                cb();
            }

            /* We are mis-using postRender here to ensure all
             * networked-component updates have been called.
             * Remove the old binding first to prevent stacking across room switches. */
            if (this.boundNetworkUpdate) {
                this.engine.scene.onPostRender.remove(this.boundNetworkUpdate);
            }
            this.boundNetworkUpdate = networkManager.update.bind(networkManager);
            this.engine.scene.onPostRender.add(this.boundNetworkUpdate);

            // Register the event listener only once to prevent duplicate event handling
            if (!this.boundOnEvent) {
                this.boundOnEvent = this.onEvent.bind(this);
                networkManager.onEvent.add(this.boundOnEvent);
            }

            //this.attachReceiveLogging();
            this.playerNetworkId = joinEvent[0];
            this.playerUserId = joinEvent[0];

            UserMessaging.instance.notifyState('Connected to server.');

            // Load TURN servers for video sharing peer connections (background task)
            this.loadTurnIceServers().catch((err) => 
                Logger.warn('[EventsClient] Failed to load TURN servers', MODULE, err)
            );

            this.startMediasoupReceiving().catch((err) =>
                Logger.warn(MODULE, 'Failed to start mediasoup receiving on connect', err)
            );

            // Mark connection as ready
            this.isConnected = true;

            // Flush any pending orchestrated requests that were queued before connection
            if (this.pendingOrchestratedRequests.length > 0) {
                Logger.info(
                    `Flushing ${this.pendingOrchestratedRequests.length} pending orchestrated request(s)`,
                    MODULE
                );
                for (const request of this.pendingOrchestratedRequests) {
                    this.requestOrchestratedInfo(request.nametag, request.transform);
                }
                this.pendingOrchestratedRequests = [];
            }

            if (this.headlessmode) {
                Logger.info("Headless mode active -- skipping avatar setup", MODULE);
            } else {
                if (this.engine.xr && this.engine.xr.session) {
                    this.vrPlayerObject &&
                        this.vrPlayerObject.addComponent(NetworkedComponent, {
                            networkId: this.playerNetworkId,
                            mode: 'send',
                        });
                    networkManager.client!.sendViaWs({
                        type: Events.USER_ENTER_XR,
                        data: {},
                    });
                } else {
                    this.playerObject &&
                        this.playerObject.addComponent(NetworkedComponent, {
                            networkId: this.playerNetworkId,
                            mode: 'send',
                        });
                }

                this.activeLeftHandObject?.addComponent(NetworkedComponent, {
                    networkId: joinEvent[1],
                    mode: 'send',
                });
                this.activeRightHandObject?.addComponent(NetworkedComponent, {
                    networkId: joinEvent[2],
                    mode: 'send',
                });
            }

            /* Start updating! */
            this.active = true;
        } catch (e) {
            Logger.error('Error while trying to join', MODULE, e);
        }
    }

    /** Attach a debug logger for incoming transform packets from the server. */
    private attachReceiveLogging() {
        if (this.receiveLoggingAttached) return;
        if (!networkManager) return;

        const nm: any = networkManager as any;
        const originalUpdateReceive = nm._updateReceive?.bind(nm);
        if (typeof originalUpdateReceive !== 'function') {
            Logger.warn('NetworkManager _updateReceive not available for logging', MODULE);
            return;
        }

        nm._updateReceive = () => {
            const client = nm.client;
            const received = client?.receivedData;
            const count = Array.isArray(received) ? received.length : 0;

            if (count > 0) {
                const latest = received[count - 1];
                if (latest instanceof ArrayBuffer) {
                    const u32 = new Uint32Array(latest);
                    const packetCount = (latest.byteLength - 4) / (8 * 4 + 4);
                    Logger.info(
                        MODULE,
                        `Received transform packet bytes=${latest.byteLength} objects=${packetCount} ts=${u32[0]}`
                    );
                } else if (ArrayBuffer.isView(latest)) {
                    const buf = latest.buffer.slice(latest.byteOffset, latest.byteOffset + latest.byteLength);
                    const u32 = new Uint32Array(buf);
                    const packetCount = (buf.byteLength - 4) / (8 * 4 + 4);
                    Logger.info(
                        MODULE,
                        `Received transform packet (view) bytes=${buf.byteLength} objects=${packetCount} ts=${u32[0]}`
                    );
                } else {
                    Logger.info(MODULE, `Received non-binary packet type=${typeof latest}`);
                }
            }

            originalUpdateReceive();
        };

        this.receiveLoggingAttached = true;
    }

    override onEvent(e: WonderlandWebsocketEvent) {
        handleNetworkEvent(this, e);
    }

    notify(text: string) {
        if (!this.notificationText) return;
        this.notificationText.text = text;
    }

    setHapticDisplayText(text: string) {
        if (!this.hapticDisplayText) return;
        this.hapticDisplayText.text = text;
    }

    setHapticDisplayEnabled(enabled: boolean) {
        this.hapticDisplayEnabled = enabled;
        if (this.hapticDisplayObject) {
            this.hapticDisplayObject.active = enabled;
            if (enabled) {
                this.hapticDisplayObject.getComponent(NotificationTextPositioner)?.refreshPosition();
                HapticInfluenceDisplay.instance.invalidateCache();
                HapticInfluenceDisplay.instance.tick();
            }
        }
        if (!enabled && this.hapticDisplayText) {
            this.hapticDisplayText.text = '';
        }
    }

    isHapticDisplayEnabled(): boolean {
        return this.hapticDisplayEnabled;
    }

    /**
     * Load TURN ice servers asynchronously with caching.
     * Ensures multiple calls don't trigger multiple fetches.
     */
    async loadTurnIceServers(): Promise<RTCIceServer[] | null> {
        if (this.turnIceServers && !this.isTurnConfigExpired(this.turnIceServers)) {
            return this.turnIceServers;
        }
        if (this.turnIceServersPromise) {
            return this.turnIceServersPromise;
        }

        this.turnIceServersPromise = this.fetchTurnIceServers();
        this.turnIceServers = await this.turnIceServersPromise;
        this.turnIceServersPromise = null;
        return this.turnIceServers;
    }

    private isTurnConfigExpired(iceServers: RTCIceServer[] | null): boolean {
        if (!iceServers?.length) {
            return true;
        }

        const nowSec = Math.floor(Date.now() / 1000);
        for (const server of iceServers) {
            if (typeof server.username === 'string') {
                const exp = Number(server.username.split(':')[0]);
                if (!Number.isNaN(exp) && exp > 0) {
                    const diffSec = exp - nowSec;
                    const isExpired = diffSec < 5 * 60;
                    return isExpired;
                }
            }
        }

        if (this.turnIceServersFetchedAt) {
            const ageMsecs = Date.now() - this.turnIceServersFetchedAt;
            const isExpired = ageMsecs > 5 * 60 * 1000;
            return isExpired;
        }

        return false;
    }

    /**
     * Fetch TURN ice servers from the configured endpoint.
     */
    private async fetchTurnIceServers(): Promise<RTCIceServer[] | null> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 4000);

            const protocol = this.secure === false ? 'http' : 'https';
            const port = this.serverPort ? `:${this.serverPort}` : '';
            const turnUrl = `${protocol}://${this.serverHost}${port}/api/turn-credentials`;

            const response = await fetch(turnUrl, {
                method: 'GET',
                headers: {
                    'accept': 'application/json',
                },
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                Logger.warn('TURN config fetch failed:', MODULE, {
                    status: response.status,
                    statusText: response.statusText
                });
                return null;
            }

            const payload = (await response.json()) as { iceServers?: RTCIceServer[] };
            
            if (!payload?.iceServers?.length) {
                Logger.warn('TURN config missing or empty iceServers', MODULE);
                return null;
            }

            // Ensure all TURN servers have credentialType set (required for TURN auth)
            payload.iceServers = payload.iceServers.map(server => ({
                ...server,
                credentialType: (server as any).credentialType || 'password'
            }));

            Logger.debug('TURN config fetched successfully', MODULE, {
                count: payload.iceServers.length
            });

            this.turnIceServersFetchedAt = Date.now();

            return payload.iceServers;
        } catch (error) {
            Logger.error('[EventsClient] TURN config fetch error:', MODULE, {
                error: error,
                message: error instanceof Error ? error.message : String(error)
            });
            return null;
        }
    }

    async handleVidshareOffer(senderId: number, offer: any) {
        await this.loadTurnIceServers().catch((err) =>
            Logger.warn('[EventsClient] Failed to load TURN servers', MODULE, err)
        );
        await handleVidshareOfferVideo(this, senderId, offer);
    }

    createVidrecPeerConnection(senderId: number) {
        return createVidrecPeerConnectionVideo(this, senderId);
    }

    //Loop through all known peers and create a peer connection for each one
    createVidsharePeerConnections() {
        createVidsharePeerConnectionsVideo(this);
    }

    createVidsharePeerConnection(playerId: number) {
        return createVidsharePeerConnectionVideo(this, playerId);
    }

    async acceptVidshareOffer(senderId: number, offer: any) {
        await acceptVidshareOfferVideo(this, senderId, offer);
    }

    async startVidshareNegotiations() {
        await startVidshareNegotiationsVideo(this);
    }

    async startVidshareNegotiation(rec: number, vidsharePC: RTCPeerConnection) {
        await startVidshareNegotiationVideo(this, rec, vidsharePC);
    }

    async createVidshareOffer(vidsharePC: RTCPeerConnection) {
        return await createVidshareOfferVideo(vidsharePC);
    }

    claimMasterShareRole() {
        claimMasterShareRoleVideo(this);
    }

    stopVidshare() {
        const client = networkManager.client as CustomClient | undefined;
        client?.setBroadcastShareAudioStream(null);

        if (this.mediasoupClient?.isBroadcasting()) {
            void this.stopMediasoupBroadcast();
            return;
        }

        if (this.serverBasedBroadcast) {
            void this.stopMediasoupBroadcast();
            return;
        }

        stopVidshareVideo(this);
    }

    destroyVidshareConnection(peerId: number) {
        destroyVidshareConnectionVideo(this, peerId);
    }

    
    destroyVidrecConnection(peerId: number) {
        destroyVidrecConnectionVideo(this, peerId);
    }

    /**
     * Register a NetworkedGrabbable component so it can receive remote grab events.
     * Called by NetworkedGrabbable components during their start() lifecycle.
     * 
     * @param networkId - Unique network ID for the grabbable object
     * @param component - The NetworkedGrabbable component to register
     */
    registerNetworkedGrabbable(networkId: number, component: NetworkedGrabbable): void {
        if (this.networkedGrabbables.has(networkId)) {
            Logger.warn(`NetworkedGrabbable with networkId ${networkId} already registered. Overwriting.`, MODULE);
        }
        this.networkedGrabbables.set(networkId, component);
        Logger.debug(`Registered NetworkedGrabbable with networkId ${networkId}`, MODULE);
    }

    /**
     * Register a NetworkedOrchestrated component so it can receive remote orchestrated events.
     * Called by NetworkedOrchestrated components during their start() lifecycle.
     * 
     * @param networkId - Unique network ID for the orchestrated object
     * @param component - The NetworkedOrchestrated component to register
     */
    registerNetworkedOrchestrated(networkId: number, component: Orchestrated): void {
        if (this.networkedOrchestrated.has(networkId)) {
            Logger.warn(`NetworkedOrchestrated with networkId ${networkId} already registered. Overwriting.`, MODULE);
        }
        this.networkedOrchestrated.set(networkId, component);
        Logger.debug(`Registered NetworkedOrchestrated with networkId ${networkId}`, MODULE);
    }

    /**
     * Unregister a NetworkedGrabbable component.
     * Called by NetworkedGrabbable components during cleanup.
     * 
     * @param networkId - Unique network ID for the grabbable object
     */
    unregisterNetworkedGrabbable(networkId: number): void {
        if (this.networkedGrabbables.delete(networkId)) {
            Logger.debug(`Unregistered NetworkedGrabbable with networkId ${networkId}`, MODULE);
        }
    }

    /**
     * Unregister a NetworkedOrchestrated component.
     * Called by NetworkedOrchestrated components during cleanup.
     * 
     * @param networkId - Unique network ID for the orchestrated object
     */
    unregisterNetworkedOrchestrated(networkId: number): void {
        if (this.networkedOrchestrated.delete(networkId)) {
            Logger.debug(`Unregistered NetworkedOrchestrated with networkId ${networkId}`, MODULE);
        }
    }
}
